import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography, Paper, Button, Box, FormHelperText, CircularProgress, Alert,
  FormControlLabel, Checkbox,
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import axios from '../utils/api';
import ServiceProviderFormFields from './ServiceProviderFormFields';
import {
  ServiceProviderFormState,
  ServiceProviderValidationErrors,
  emptyServiceProviderForm,
  providerToFormState,
  validateServiceProviderForm,
  buildServiceProviderPayload,
  scrollServiceProviderToFirstError,
} from './serviceProviderShared';

const EditServiceProviderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<ServiceProviderFormState>(emptyServiceProviderForm());
  const [errors, setErrors] = useState<ServiceProviderValidationErrors>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicateFlagged, setDuplicateFlagged] = useState(false);
  const [duplicateReason, setDuplicateReason] = useState<string | null>(null);

  const clearError = (field: keyof ServiceProviderValidationErrors) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`/api/service-providers/${id}`);
        setForm(providerToFormState(response.data));
        setDuplicateFlagged(!!response.data.duplicateFlagged);
        setDuplicateReason(response.data.duplicateReason || null);
      } catch (err: any) {
        setLoadError(err?.response?.data?.error || 'Failed to load agency');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleSubmit = async () => {
    const validationErrors = validateServiceProviderForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollServiceProviderToFirstError(validationErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await axios.put(`/api/service-providers/${id}`, {
        ...buildServiceProviderPayload(form),
        duplicateFlagged,
      });
      navigate(`/management/agency/${id}`);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.error || 'Failed to update agency.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/management')}>Back to Management</Button>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Edit Agency</Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/management')}>Back</Button>
      </Box>

      {duplicateReason && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {duplicateReason}
        </Alert>
      )}

      <form onSubmit={(e) => e.preventDefault()}>
        <ServiceProviderFormFields form={form} setForm={setForm} errors={errors} clearError={clearError} />

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            control={<Checkbox checked={duplicateFlagged} onChange={(e) => setDuplicateFlagged(e.target.checked)} />}
            label="Flag as possible duplicate (uncheck to clear the flag after review)"
          />
        </Box>

        {submitError && (
          <Box sx={{ mt: 2 }}>
            <FormHelperText error sx={{ fontSize: '1rem' }}>{submitError}</FormHelperText>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} startIcon={<Save />} size="large">
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default EditServiceProviderForm;
